import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Nuclias from "const/nuclias";

import { isNil, cloneDeep } from 'lodash';
import { SearchType } from "filter-data";

const { licenseConfig, deviceType } = Nuclias;

export const defaultMessages = {
  success: null,
  error: null,
  warning: null,
};

export const defaultColumns = (isOrg) => {
  const list = [
    {
      label: '5dddd8b61e',
      accessor: 'licenseTypeLabelFull',
      sortable: true,
      sortbyOrder: 'asc',
    },
    {
      label: 'ec53a8c4f0',
      accessor: 'licenseStateLabelFull',
      sortable: true,
    },
    {
      label: '5d5c4ad5db',
      accessor: 'licenseKey',
      sortable: true,
    },
    {
      label: 'cf5f3091e3',
      accessor: 'termDays',
      sortable: true,
      downloadName: 'termUnitLabelFull',
    },
    {
      label: '768d0c0b0b',
      accessor: 'deviceTypeLabelFull',
      sortable: true,
    },
    {
      label: '8a6ce18ee4',
      accessor: 'releasedAtUnix',
      sortable: true,
      downloadName: 'releasedAtFormat',
    },
    // msp
    {
      label: 'd4b0e26d3f',
      accessor: 'preOrganizationName',
      sortable: true,
      hide: isOrg,
    },
    {
      label: 'c1ca926603',
      accessor: 'organizationName',
      sortable: true,
      hide: isOrg,
    },
    {
      label: 'c9772a79fb',
      accessor: 'orgUid',
      sortable: true,
      hide: isOrg,
    },
    {
      label: '91f867cbb6',
      accessor: 'orgTypeLabel',
      sortable: true,
      hide: isOrg,
    },
    // msp
    {
      label: '720146c2df',
      accessor: 'deviceUid',
      sortable: true,
    },
    {
      label: '402d4d2bce',
      accessor: 'deviceModelName',
      sortable: true,
    },
    {
      label: '3121f87028',
      accessor: 'macAddress',
      sortable: true,
    },
    {
      label: 'db3794c7d7',
      accessor: 'startDateUnix',
      sortable: true,
      downloadName: 'startDateFormat',
    },
    {
      label: '4d29676394',
      accessor: 'endDateUnix',
      sortable: true,
      downloadName: 'endDateFormat',
    },
    {
      label: '262c080f5c',
      accessor: 'timeRemaining',
      sortable: true,
      downloadName: 'remainDayUnitLabelFull',
    },
  ];
  const result = (list).filter((item, index) => {
    return isNil(item.hide) || !item.hide;
  });
  return result;
};

export const defaultModalStatus = {
  assignTo: {
    status: false,
    disabled: false,
  },
  addLicense: {
    status: false,
    disabled: false,
  },
  revoke: {
    status: false,
    disabled: false,
  },
  suspend: {
    status: false,
    disabled: false,
  },
  resume: {
    status: false,
    disabled: false,
  },
  generate: {
    status: false,
  }
};

export const dropdownLicenseTermList = () => {
  const list = licenseConfig.terms.map((item, index) => {
    item.title = item.name;
    return item;
  });
  return cloneDeep(list);
};

const getDefaultFilterOptions = (t) => {
  const list = {
    licenseType: {
      name: ('5dddd8b61e'),
      searchType: SearchType.EQ,
      data: [
        { title: 'b1c94ca2fb', value: null, isActive: true }, // or ''
        { title: '8a73657240', value: licenseConfig.usedTypes.unused, isActive: false },
        { title: 'c29fb273b8', value: licenseConfig.usedTypes.used, isActive: false },
      ]
    },
    status: {
      name: ('ec53a8c4f0'),
      searchType: SearchType.EQ,
      data: [
        { title: 'b1c94ca2fb', value: null, isActive: true },
        { title: '4d3d769b81', value: licenseConfig.statusTypes.active, isActive: false },
        { title: '3cab03c00d', value: licenseConfig.statusTypes.inactive, isActive: false },
        { title: 'b1b0643f87', value: licenseConfig.statusTypes.suspended, isActive: false },
      ]
    },
    licenseDeviceType: {
      name: ('768d0c0b0b'),
      searchType: SearchType.EQ,
      data: [
        { title: 'b1c94ca2fb', value: null, isActive: true },
        { title: 'cb3f9a1103', value: deviceType.ap, isActive: false },
        { title: 'bbc155fb2b', value: deviceType.switch, isActive: false },
        { title: '926dec9494', value: deviceType.gateway, isActive: false }
      ]
    },
    timeRemaining: {
      name: ('4d29676394'),
      searchType: SearchType.LTE,
      data: [
        { title: 'b1c94ca2fb', value: null, isActive: true },
        { title: t('b314cb794c', { day: 1 }), value: 1, isActive: false },
        { title: t('14a126cf45', { days: 7 }), value: 7, isActive: false },
        { title: t('14a126cf45', { days: 14 }), value: 14, isActive: false },
        { title: t('14a126cf45', { days: 30 }), value: 30, isActive: false },
        { title: t('14a126cf45', { days: 60 }), value: 60, isActive: false },
      ]
    },
    licenseKey: {
      name: ('5d5c4ad5db'),
      searchType: SearchType.LK,
      data: [
        { title: '', value: '', isActive: true },
      ]
    },
    // MSP
    searchOrgId: {
      name: ('c1ca926603'),
      // searchType: SearchType.LK,
      data: [
        { title: '', value: null, isActive: true },
      ]
    },
  };
  return cloneDeep(list);
};



const useConst = () => {
  const { t } = useTranslation();
  const [filterOptions, setFilterOptions] = useState(getDefaultFilterOptions(t));

  const changeOption = (target, type) => {
    let data = null;
    if (type === 'searchOrgId') {
      data = filterOptions[type].data[0];
      data.value = target.id;
      data.title = target.name;
    } else {
      for (const [index, item] of filterOptions[type].data.entries()) {
        if (target.value === item.value) {
          item.isActive = true;
          data = item;
        } else {
          item.isActive = false;
        }
      }
    }
    return data;
  };

  const changeMultiOption = (list) => {
    const result = [];
    const edieOption = (targetItem, key, obj) => {
      for (const [index, item] of obj.data.entries()) {
        item.isActive = targetItem.value === item.value ? true : false;
        if (targetItem.value === item.value) {
          item.isActive = true;
          let copy = cloneDeep(item);
          copy.type = key;
          result.push(copy);
        } else {
          item.isActive = false;
        }
      }
    };

    for (const [targetIndex, targetItem] of list.entries()) {
      for (const [key, obj] of Object.entries(filterOptions)) {
        if (targetItem.type === key) {
          edieOption(targetItem, key, obj);
        }
      }
    }
    return result;
  };

  const resetFilterOptions = () => {
    setFilterOptions(getDefaultFilterOptions(t));
  };

  const translateList = (list) => {
    return new Promise(async (Resolve, Reject) => {
      let newList = cloneDeep(list);
      for (const [index, item] of newList.entries()) {
        for (let [key, value] of Object.entries(item)) {
          if (key.toLocaleLowerCase().indexOf('label') !== -1 && key.toLocaleLowerCase().indexOf('full') === -1) {
            if (key === 'termUnitLabel') {
              let label = `${item.period} ${t(value)}`;
              label += item.isFree ? ` (${t('6a4d6f2ef0')})` : '';
              newList[index][`${key}Full`] = label;
            } else if (key === 'remainDayUnitLabel') {
              if (item.status === licenseConfig.statusTypes.inactive) {
                newList[index][`${key}Full`] = ``;
              } else {
                newList[index][`${key}Full`] = `${item.timeRemaining} ${t(value)}`;
              }
            } else {
              newList[index][`${key}Full`] = t(value);
            }
          }
        }
      }
      Resolve(newList);
    });
  };

  return { filterOptions, resetFilterOptions, changeOption, changeMultiOption, translateList };
};
export default useConst;